import React from "react";
import { X, Droplets, Zap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const Marker = ({ filled }) => (
  <span
    aria-hidden
    className={`inline-block w-3 h-3 ${
      filled ? "bg-[#00A651]" : "bg-white/15"
    }`}
  />
);

const LatexComparisonDialog = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const c = t.latex_compare;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="latex-compare-dialog"
        className="bg-[#0A0A0A] border border-[#00A651]/30 text-white rounded-none max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="relative p-8 md:p-10">
          <DialogClose
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#00A651] hover:text-[#00A651] transition-colors z-10"
            data-testid="latex-compare-close"
            aria-label={t.close}
          >
            <X className="w-4 h-4" />
          </DialogClose>

          <div className="inline-block bg-[#00A651] text-black font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-5">
            Latex
          </div>

          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-brush text-4xl md:text-5xl text-[#00A651] leading-tight uppercase">
              {c.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 font-condensed uppercase tracking-widest text-xs">
              {c.subtitle}
            </DialogDescription>
          </DialogHeader>

          <p className="mt-6 text-white/80 text-base leading-relaxed">{c.intro}</p>

          {/* Two cards side-by-side */}
          <div className="mt-8 grid md:grid-cols-2 gap-[1px] bg-white/10 border border-white/10">
            {c.cards.map((card, idx) => {
              const Icon = card.key === "giga_grip" ? Droplets : Zap;
              return (
                <div
                  key={card.key}
                  data-testid={`latex-card-${card.key}`}
                  className="bg-black p-6"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="font-condensed uppercase tracking-widest text-[10px] text-[#00A651]">
                        {card.edition}
                      </div>
                      <h3 className="font-brush text-2xl text-white leading-none mt-2">
                        {card.name}
                      </h3>
                      <div className="font-condensed uppercase tracking-widest text-[11px] text-white/50 mt-1">
                        {card.tagline}
                      </div>
                    </div>
                    <Icon className="w-6 h-6 text-[#00A651] shrink-0" />
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">{card.description}</p>
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <div className="font-condensed uppercase tracking-widest text-[10px] text-white/40 mb-1">
                      Best for
                    </div>
                    <div className="text-white/80 text-sm">{card.best_for}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison ratings table */}
          <div className="mt-8">
            <div className="font-condensed uppercase tracking-widest text-xs text-white/50 mb-3">
              {c.categories_label}
            </div>
            <div className="border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[1fr,auto,auto] gap-x-4 md:gap-x-6 px-4 py-3 bg-black/60 border-b border-white/10 font-condensed uppercase tracking-widest text-[10px] text-white/50">
                <span>Kategorie</span>
                <span className="text-center">Giga</span>
                <span className="text-center">Contact</span>
              </div>
              {Object.entries(c.ratings).map(([key, row]) => (
                <div
                  key={key}
                  data-testid={`latex-rating-${key}`}
                  className="grid grid-cols-[1fr,auto,auto] gap-x-4 md:gap-x-6 px-4 py-3 border-b border-white/5 items-center"
                >
                  <span className="font-condensed uppercase tracking-wide text-sm text-white/80">
                    {row.label}
                  </span>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Marker key={i} filled={i < row.giga} />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Marker key={i} filled={i < row.contact} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-5 text-sm text-white/60 italic border-l-2 border-[#00A651] pl-4">
            {c.footer}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LatexComparisonDialog;
